import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, Loader2, Send, Check, Wand2, ImagePlus, Paintbrush, Minus, Plus, Trash2, Crosshair, Search, Sparkles, Maximize2, Download, Rewind, Zap, Palette, ChevronDown } from 'lucide-react';
import { toast } from '../utils/toast';
const API = window.__API_BASE + '/api';

/**
 * Iteration Workbench — unified visual modal for keyframe + AI Photoshop
 * editing. Originally Gemini-only; extended 2026-05-13 (Rohit) with a
 * `mode` prop so the AI Photoshop recipe can re-use the exact same UX
 * instead of the legacy split SketchSamModal layout.
 *
 * Modes:
 *   mode='keyframe'  (default) — iterates `/shots/{id}/iterate-keyframe`
 *                                target_field selects keyframe vs hyperreal
 *   mode='photoshop'           — iterates `/shots/{id}/sketch-sam-iterate`
 *                                full-image text-only edits, no SAM points
 *
 * engineLocked=true   — hides the per-iteration engine picker (used by
 *                       Insanely Good Sketches / Keyframes which are
 *                       pinned to gpt-image-2).
 * onAddToGallery      — when set, renders a "★ Add to Gallery" button.
 *                       Pass-through callback; parent does the POST.
 */
export default function GeminiIterationModal({
  shotId, shot, onClose, onSelect,
  aspectClass = 'aspect-video',
  preselectedEngine,
  targetField = 'keyframe_url',
  mode = 'keyframe',
  engineLocked,
  onAddToGallery,
  initialInstruction = '',
}) {
  // Endpoint config — single source of truth so adding modes later is a
  // 5-line diff rather than ripping through 20 call sites.
  const isPhotoshop = mode === 'photoshop';
  const endpoints = useMemo(() => (isPhotoshop ? {
    list:   (id) => `/shots/${id}/sketch-sam-iterations`,
    iter:   (id) => `/shots/${id}/sketch-sam-iterate`,
    select: (id) => `/shots/${id}/select-sketch-iteration`,
  } : {
    // For keyframes the LIST endpoint uses a target query param so the
    // hyperreal sub-stack reuses the same iteration history table.
    list:   (id) => {
      const t = targetField === 'keyframe_hyperreal_url' ? 'hyperreal' : 'keyframe';
      return `/shots/${id}/gemini-iterations?target=${t}`;
    },
    iter:   (id) => `/shots/${id}/iterate-keyframe`,
    select: (id) => `/shots/${id}/select-gemini-iteration`,
  }), [isPhotoshop, targetField]);
  // Engine picker visibility — matches IGS SAM workbench (SketchSamModal):
  // shown by default, hidden only when `engineLocked=true` is explicitly
  // passed (used by Insanely Good Sketches / Insanely Good Keyframes pipes
  // that pin a single engine end-to-end).
  // 2026-05-17: dropped the `isPhotoshop` precondition so the IGK edit
  // workbench surfaces the same gpt-image-2 ↔ nano-banana toggle that
  // already exists in IGS SAM. The two outer entry buttons on the
  // keyframe card (GPT / Gemini) pre-select an engine; the picker now
  // also lets the operator flip engine mid-session without closing.
  const showEnginePicker = !engineLocked;
  const [engineChoice, setEngineChoice] = useState(() => {
    const fromShot = (shot?.edit_engine || '').toLowerCase();
    if (fromShot === 'gpt-image-2' || fromShot === 'nano-banana') return fromShot;
    if (preselectedEngine === 'nano-banana') return 'nano-banana';
    return 'gpt-image-2';
  });

  const [iterations, setIterations] = useState([]);
  const [originalUrl, setOriginalUrl] = useState('');
  const [currentUrl, setCurrentUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [iterating, setIterating] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(initialInstruction || '');
  // Up to 3 reference images. Each entry: { b64: '...', preview: 'data:...' }
  // Replaces the older single refImage/refPreview state (Feb 2026).
  // The AI Photoshop workbench treats each ref as a separate "style guide"
  // and forwards them all to the backend as `reference_images_b64: [...]`.
  // We keep `refImage` as a derived alias for the first slot so the
  // onKeyDown handler stays simple (Enter-to-submit when at least one
  // ref OR some feedback text is present).
  const [refImages, setRefImages] = useState([]);  // [{b64, preview}, ...] max 3
  const refImage = refImages[0]?.b64 || null;
  // Friendly per-iteration action label (May 2026) — replaces the verbose
  // "feedback prompt" caption (often a 500-char ALL-CAPS Director's Prompt)
  // with a short, operator-meaningful summary like "Skin enhanced via
  // Enhancor" or "Colour: S-Log3 flat grade". The full prompt is still
  // available on hover via the title attribute.
  const friendlyActionLabel = useCallback((iter) => {
    const engine = ((iter && iter.engine) || '').toLowerCase();
    if (engine.startsWith('enhancor-')) {
      if (engine.includes('realistic_skin'))    return 'Skin enhanced (Enhancor)';
      if (engine.includes('portrait_upscaler')) return 'Portrait upscaled (Enhancor)';
      if (engine.includes('general_upscaler'))  return 'Upscaled (Enhancor)';
      return 'Enhanced (Enhancor)';
    }
    const fb = ((iter && (iter.feedback || iter.prompt_used)) || '').toLowerCase();
    // Colour preset fingerprints (matched against the canned preset prompts)
    if (fb.includes('s-log3') || fb.includes('slog3'))                  return 'Colour: S-Log3 flat grade';
    if (fb.includes('acescct') || (fb.includes('aces') && fb.includes('neutral'))) return 'Colour: ACES neutral';
    if (fb.includes('teal-orange') || fb.includes('teal/orange') || (fb.includes('teal') && fb.includes('orange'))) return 'Colour: Teal-orange cinematic';
    if (fb.includes('5600k'))                                            return 'Colour: Soft daylight 5600K';
    if (fb.includes('golden-hour') || fb.includes('golden hour'))        return 'Colour: Golden hour warm';
    if (fb.includes('night') && fb.includes('blue'))                     return 'Colour: Night / low-light blue';
    if (fb.includes('black-and-white') || fb.includes('b&w') || fb.includes('salgado')) return 'Colour: B&W conversion';
    // Generic iteration — show first 80 chars of the actual feedback,
    // sentence-cased (don't shout in ALL CAPS).
    const raw = ((iter && (iter.feedback || iter.prompt_used)) || '').trim();
    if (raw) {
      const trimmed = raw.length > 80 ? raw.slice(0, 80).trim() + '…' : raw;
      const normalised = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
      return `Iterated: ${normalised}`;
    }
    return 'Iterated';
  }, []);

  const [selectedImage, setSelectedImage] = useState(null);

  // ── Enhancor AI integration (May 2026) ────────────────────────────────
  // Operator can fire any of 3 Enhancor services on the currently-displayed
  // frame. The result lands as a new version in shot.<target>_versions[]
  // with active=true so it auto-becomes the next "Rn" chip. Status polls
  // every 4s while a job is in-flight.
  const [enhanceOpen, setEnhanceOpen] = useState(false);  // dropdown open
  const [enhancePending, setEnhancePending] = useState(null);  // instant feedback — set to service slug the moment the button is clicked
  const [enhanceJob, setEnhanceJob] = useState(null);  // {request_id, status, service}
  const enhancePollRef = useRef(null);
  const ENHANCOR_SERVICES = [
    { id: 'realistic_skin',    label: 'Realistic Skin',     desc: 'Face / skin polish + cleanup (~30-90s)' },
    { id: 'portrait_upscaler', label: 'Portrait Upscale',   desc: 'Face-aware upscale, professional mode (~45s)' },
    { id: 'general_upscaler',  label: 'General Upscale',    desc: 'Universal detail-add upscale (~30s)' },
  ];

  // ── Colour Presets (May 2026) ─────────────────────────────────────────
  // Click → fills the iterate textbox with a canned colour-grade prompt.
  // Operator still has to click Iterate, which keeps the standard engine /
  // mask / ref-image flow intact.
  const [presetsOpen, setPresetsOpen] = useState(false);
  // Tracks whether the textbox content was loaded from a Colour Preset
  // (set by the dropdown click). When true, the next Iterate dispatch sends
  // `colour_grade_only: true` so the backend skips cast/costume/env refs
  // (those references derail gpt-image-2 into reconstructing the scene
  // instead of recolouring the source — verified live bug 2026-05-27 PM).
  // Cleared after any successful iterate OR when the operator edits the
  // textbox manually past the preset prompt.
  const [colourPresetActive, setColourPresetActive] = useState(null);
  const COLOUR_PRESETS = [
    { id: 'slog3',     label: 'S-Log3 flat grade',     prompt: "Convert this image to S-Log3 colour space — flat log gamma curve, 14-stop dynamic range, neutral grade, lifted shadows, no highlight clipping. Preserve all skin tones and detail. The output should look ungraded / log-flat, ready for downstream colour grading." },
    { id: 'aces',      label: 'ACES neutral',          prompt: "Apply an ACES neutral conversion — ACEScct working space, neutral display rendering, slight contrast, restored highlight roll-off, natural skin tones. The image should feel cinematic but unstylised." },
    { id: 'tealorange',label: 'Cinematic teal-orange', prompt: "Apply a classic Hollywood teal-orange grade — push shadows toward cool teal/cyan, lift midtones and highlights into warm orange, complementary colour balance, slight S-curve contrast, preserve clean skin tones in the orange band. Cinematic blockbuster look." },
    { id: 'daylight',  label: 'Soft daylight 5600K',   prompt: "Re-grade to a balanced 5600K daylight white point — neutral overall colour temperature, soft shadows, natural skin, clean whites, no colour cast. Bright but not blown out." },
    { id: 'golden',    label: 'Golden hour warm',      prompt: "Apply a warm golden-hour grade — push white balance to ~3200-3500K, lift midtones into amber/gold, soft long shadows, subtle bloom on highlights, romantic late-afternoon ambient feel." },
    { id: 'night',     label: 'Night / low-light blue', prompt: "Convert to a night / low-light grade — shift white balance to cool blue, push shadows deep but not crushed, preserve highlight specular details (streetlights, signage), preserve faces with subtle key fill, add a hint of grain. Cinematic night-time mood." },
    { id: 'bw',        label: 'B&W conversion',        prompt: "Convert this image to a high-contrast black-and-white image — full tonal range, deep blacks, clean whites, retain skin texture, slight grain. Think Sebastião Salgado / classic photojournalism feel." },
  ];

  // Map modal mode/targetField → backend target_field for Enhancor versions array
  const enhancorTargetField = (() => {
    if (isPhotoshop) return 'sketch_versions';
    if (targetField === 'keyframe_hyperreal_url') return 'keyframe_hyperreal_versions';
    return 'keyframe_versions';
  })();

  const triggerEnhance = useCallback(async (service) => {
    if (!currentUrl) {
      toast.error('No image loaded — cannot enhance');
      return;
    }
    // Flip the pending flag synchronously so the spinner appears on the
    // same paint cycle as the click. Without this we'd wait the full
    // ~1.5s round-trip to /enhance before the operator sees any feedback.
    setEnhancePending(service);
    setEnhanceOpen(false);
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/shots/${shotId}/enhance`,
        {
          service,
          source_url: currentUrl,
          target_field: enhancorTargetField,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const { job_id, request_id, status: jobStatus } = resp.data;
      setEnhanceJob({ jobId: job_id, requestId: request_id, status: jobStatus || 'IN_QUEUE', service });
      toast.success(`Enhancor ${service.replace('_', ' ')} queued`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'failed to queue';
      toast.error(`Enhance failed: ${msg}`);
    } finally {
      setEnhancePending(null);
    }
  }, [currentUrl, shotId, enhancorTargetField]);

  // Poll /enhancement-jobs every 4s while we have an in-flight job
  useEffect(() => {
    if (!enhanceJob || ['COMPLETED', 'FAILED', 'TIMEOUT'].includes(enhanceJob.status)) {
      if (enhancePollRef.current) { clearInterval(enhancePollRef.current); enhancePollRef.current = null; }
      return;
    }
    if (enhancePollRef.current) return;
    enhancePollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/shots/${shotId}/enhancement-jobs`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        const jobs = r.data?.jobs || [];
        const ours = jobs.find(j => j.request_id === enhanceJob.requestId);
        if (!ours) return;
        if (ours.status !== enhanceJob.status) {
          setEnhanceJob(prev => ({ ...prev, status: ours.status }));
          if (ours.status === 'COMPLETED') {
            toast.success(`Enhancor ${enhanceJob.service.replace('_', ' ')} ready — added as new version`);
            // Refresh iterations list so the new version chip appears
            try {
              const iters = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api${endpoints.list(shotId)}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
              );
              setIterations(iters.data?.iterations || iters.data || []);
            } catch {}
            if (ours.result_url_s3) setCurrentUrl(ours.result_url_s3);
          } else if (ours.status === 'FAILED' || ours.status === 'TIMEOUT') {
            toast.error(`Enhancor ${enhanceJob.service.replace('_', ' ')} ${ours.status.toLowerCase()}: ${(ours.error || '').slice(0, 120)}`);
          }
        }
      } catch {}
    }, 4000);
    return () => { if (enhancePollRef.current) { clearInterval(enhancePollRef.current); enhancePollRef.current = null; } };
  }, [enhanceJob, shotId, endpoints]);


  // Lightbox state (Feb 2026 — issue #2 from user's annotated screenshot):
  // each iteration tile + the top frame now has a hover-revealed "Expand"
  // and "Download" icon. Expand opens a fullscreen overlay.
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const handleDownload = useCallback(async (url, filename) => {
    try {
      const resp = await fetch(url, { credentials: 'omit' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'iteration.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
    } catch (e) {
      // S3 CORS / network fallback: open the URL in a new tab so the user
      // can still save manually.
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  // ── Option D: when the user clicks a different version chip (R0/R1/R2…),
  // load THAT version's prompt + ref thumbnails into the input fields. When
  // they stay on the same version (or iterate on it), the inputs persist.
  // Tracks the last loaded version so we don't re-load on every render.
  const lastLoadedVersionRef = React.useRef(null);

  React.useEffect(() => {
    if (!selectedImage) return;
    const versionKey = selectedImage.round ?? -1;
    if (versionKey === lastLoadedVersionRef.current) return;
    lastLoadedVersionRef.current = versionKey;

    // R-1 = original / pre-iteration source — fall back to the modal's
    // initialInstruction (the landing-page prompt) and no refs.
    if (versionKey < 0) {
      setFeedback(initialInstruction || '');
      setRefImages([]);
      return;
    }
    const iter = iterations?.[versionKey];
    if (!iter) return;
    setFeedback((iter.prompt_used || iter.feedback || '').trim());

    // Fetch each ref URL → b64 so it's re-usable in a new iterate request.
    const urls = (iter.reference_image_urls || []).slice(0, 3);
    if (urls.length === 0) {
      setRefImages([]);
      return;
    }
    (async () => {
      const next = [];
      for (const url of urls) {
        try {
          const resp = await fetch(url, { credentials: 'omit' });
          if (!resp.ok) continue;
          const blob = await resp.blob();
          const dataUrl = await new Promise((res) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.readAsDataURL(blob);
          });
          next.push({ b64: dataUrl.split(',')[1], preview: dataUrl });
        } catch (_e) { /* ignore single-ref failures */ }
      }
      setRefImages(next);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage?.round, iterations]);

  // Mask mode state
  const [maskEnabled, setMaskEnabled] = useState(false);
  const [maskTool, setMaskTool] = useState('sam'); // 'sam' or 'brush'
  const [samPrompt, setSamPrompt] = useState('');
  const [brushSize, setBrushSize] = useState(30);
  const [isPainting, setIsPainting] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [samLoading, setSamLoading] = useState(false);

  // Natural image dimensions for workbench container
  const [containerSize, setContainerSize] = useState(null);

  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);
  const maskCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const imgLoadedRef = useRef(false);

  // ── Data fetching ──
  //
  // Phase 3 keyframe-VLM parity (2026-05-23) — for the hyperreal target
  // we FIRST try `/shots/:id/keyframe/versions` so the filmstrip shows
  // every entry in `keyframe_hyperreal_versions[]` (Step 1 + Step 3 +
  // future hyperreal_iter rows) with full audit provenance. Mirror of
  // SketchSamModal's unified versions path. If the endpoint returns no
  // versions (legacy shots that haven't been backfilled, or shots that
  // haven't been generated yet), fall back to the legacy
  // `/gemini-iterations?target=hyperreal` table so existing workbench
  // history keeps rendering. The mapped rows carry `version_id` so
  // `handleAccept` knows to call `/keyframe/set-active` instead of the
  // legacy `/select-gemini-iteration`.
  const isHyperrealTarget = !isPhotoshop && targetField === 'keyframe_hyperreal_url';
  const fetchIterations = useCallback(async () => {
    try {
      // Hyperreal: try versions endpoint first.
      if (isHyperrealTarget) {
        try {
          const vres = await axios.get(`${API}/shots/${shotId}/keyframe/versions`);
          const versions = vres.data.versions || [];
          if (versions.length > 0) {
            const activeId = vres.data.active_version_id;
            const mapped = versions.map(v => ({
              version_id: v.version_id,
              source: v.source,
              // Enhancor-finalized entries write `url` instead of
              // `keyframe_url`; fall back so the strip thumb renders.
              image_url: v.keyframe_url || v.url || '',
              engine: v.engine,
              feedback: v.iterate_prompt || '',
              prompt_used: v.iterate_prompt || '',
              vlm_audit: v.vlm_audit,
              reference_image_urls: v.reference_image_urls || [],
              created_at: v.created_at,
              parent_version_id: v.parent_version_id,
            }));
            setIterations(mapped);
            setCurrentUrl(vres.data.current_keyframe_url || mapped[mapped.length - 1].image_url || '');
            setOriginalUrl('');
            // Pull legacy `iterating` flag best-effort so an in-flight
            // hyperreal_iter still pulses the workbench's spinner.
            try {
              const old = await axios.get(`${API}${endpoints.list(shotId)}`);
              setIterating(old.data.iterating || false);
              if (old.data.error) setError(old.data.error);
            } catch { /* silent */ }
            setLoading(false);
            return;
          }
        } catch { /* fall through to legacy path */ }
      }

      const res = await axios.get(`${API}${endpoints.list(shotId)}`);
      // Photoshop endpoint shape differs slightly — normalize.
      if (isPhotoshop) {
        // /sketch-sam-iterations returns { original_url, iterations: [...] }
        // (no `current_url` — the latest iteration's image_url IS current).
        const iters = res.data.iterations || [];
        const original = res.data.original_url || '';
        const latest = iters.length > 0 ? iters[iters.length - 1].image_url : original;
        setOriginalUrl(original);
        setCurrentUrl(latest);
        setIterations(iters);
        setIterating(res.data.iterating || false);
      } else {
        setOriginalUrl(res.data.original_url || '');
        setCurrentUrl(res.data.current_url || '');
        setIterations(res.data.iterations || []);
        setIterating(res.data.iterating || false);
      }
      if (res.data.error) setError(res.data.error);
    } catch {
      toast.error('Failed to load iterations');
    } finally {
      setLoading(false);
    }
  }, [shotId, targetField, isPhotoshop, isHyperrealTarget, endpoints]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchIterations();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchIterations]);

  useEffect(() => {
    if (iterating) {
      pollRef.current = setInterval(async () => {
        try {
          // For hyperreal target: refresh `keyframe_hyperreal_versions[]`
          // each tick so a freshly-landed Step 3 / hyperreal_iter row
          // shows up in the filmstrip without a full modal close+reopen.
          // Iterating flag still rides the legacy list endpoint.
          if (isHyperrealTarget) {
            try {
              const vres = await axios.get(`${API}/shots/${shotId}/keyframe/versions`);
              const versions = vres.data.versions || [];
              if (versions.length > 0) {
                const mapped = versions.map(v => ({
                  version_id: v.version_id,
                  source: v.source,
                  image_url: v.keyframe_url || v.url || '',
                  engine: v.engine,
                  feedback: v.iterate_prompt || '',
                  prompt_used: v.iterate_prompt || '',
                  vlm_audit: v.vlm_audit,
                  reference_image_urls: v.reference_image_urls || [],
                  created_at: v.created_at,
                  parent_version_id: v.parent_version_id,
                }));
                setIterations(mapped);
                setCurrentUrl(vres.data.current_keyframe_url || mapped[mapped.length - 1].image_url || '');
              }
            } catch { /* fall through to legacy refresh below */ }
          }
          const res = await axios.get(`${API}${endpoints.list(shotId)}`);
          let newIterating = res.data.iterating || false;
          if (isPhotoshop) {
            const iters = res.data.iterations || [];
            const latest = iters.length > 0 ? iters[iters.length - 1].image_url : res.data.original_url || '';
            setIterations(iters);
            setCurrentUrl(latest);
          } else if (!isHyperrealTarget) {
            // Hyperreal target already refreshed above from /keyframe/versions.
            setIterations(res.data.iterations || []);
            setCurrentUrl(res.data.current_url || '');
          }
          setIterating(newIterating);
          if (res.data.error) { setError(res.data.error); setIterating(false); }
          if (!newIterating && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            clearMask();
            if (scrollRef.current) scrollRef.current.scrollTop = 0;
          }
        } catch { /* silent */ }
      }, 4000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [iterating, shotId, targetField, isPhotoshop, isHyperrealTarget, endpoints]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Canvas setup for mask painting ──
  const setupCanvas = useCallback(() => {
    const container = containerRef.current;
    const canvas = maskCanvasRef.current;
    if (!container || !canvas) return;
    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }, []);

  useEffect(() => { if (maskEnabled) setupCanvas(); }, [maskEnabled, setupCanvas, currentUrl, originalUrl]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !maskEnabled) return;
    const ro = new ResizeObserver(() => {
      if (!hasStrokes) setupCanvas();
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [maskEnabled, setupCanvas, hasStrokes]);

  // ── Brush painting ──
  const getPoint = (e) => {
    const container = containerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const paintStroke = (x, y) => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = 'rgba(168, 85, 247, 0.45)';
    ctx.beginPath();
    ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  // ── Brush painting (only in 'brush' tool mode) ──
  const handlePointerDown = (e) => {
    if (!maskEnabled || iterating || maskTool !== 'brush') return;
    setIsPainting(true);
    setHasStrokes(true);
    const pt = getPoint(e);
    if (pt) paintStroke(pt.x, pt.y);
  };

  const handlePointerMove = (e) => {
    if (!isPainting || !maskEnabled || iterating || maskTool !== 'brush') return;
    const pt = getPoint(e);
    if (pt) paintStroke(pt.x, pt.y);
  };

  const handlePointerUp = () => setIsPainting(false);

  // ── SAM3 text-based segmentation ──
  const handleSAM3Segment = async () => {
    if (!maskEnabled || iterating || samLoading || !samPrompt.trim()) return;

    const kfUrl = selectedImage?.url || currentUrl || originalUrl;
    if (!kfUrl) return;

    setSamLoading(true);
    try {
      const res = await axios.post(`${API}/shots/${shotId}/sam3-segment`, {
        image_url: kfUrl,
        text_prompt: samPrompt.trim(),
      });
      // Draw SAM3 mask on the paint canvas
      if (res.data.mask_base64) {
        const maskImg = new Image();
        maskImg.onload = () => {
          const canvas = maskCanvasRef.current;
          const cont = containerRef.current;
          if (!canvas || !cont) return;
          const cRect = cont.getBoundingClientRect();
          const dpr = window.devicePixelRatio || 1;

          // Read mask pixels and draw purple overlay where white
          const tc = document.createElement('canvas');
          tc.width = cRect.width;
          tc.height = cRect.height;
          const tctx = tc.getContext('2d');
          tctx.drawImage(maskImg, 0, 0, cRect.width, cRect.height);
          const md = tctx.getImageData(0, 0, cRect.width, cRect.height);

          const ctx = canvas.getContext('2d');
          ctx.save();
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          for (let py = 0; py < cRect.height; py++) {
            for (let px = 0; px < cRect.width; px++) {
              const idx = (py * cRect.width + px) * 4;
              if (md.data[idx] > 128) {
                ctx.fillStyle = 'rgba(168, 85, 247, 0.45)';
                ctx.fillRect(px, py, 1, 1);
              }
            }
          }
          ctx.restore();
          setHasStrokes(true);
          const info = [];
          if (res.data.coverage_percent) info.push(`${res.data.coverage_percent}% coverage`);
          if (res.data.num_instances) info.push(`${res.data.num_instances} instance(s)`);
          toast.success(`SAM3: ${info.join(', ')}`);
        };
        maskImg.src = `data:image/png;base64,${res.data.mask_base64}`;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'SAM3 segmentation failed';
      toast.error(msg);
    } finally {
      setSamLoading(false);
    }
  };

  // ── Generate binary mask from canvas ──
  const generateMaskB64 = useCallback(() => {
    const canvas = maskCanvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !imgLoadedRef.current) return null;

    const w = img.naturalWidth;
    const h = img.naturalHeight;

    // Read raw pixel data from the mask canvas
    const srcCtx = canvas.getContext('2d');
    const srcW = canvas.width;
    const srcH = canvas.height;
    const srcData = srcCtx.getImageData(0, 0, srcW, srcH).data;

    // Create output binary mask at image resolution
    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const octx = out.getContext('2d');
    const imageData = octx.createImageData(w, h);
    const data = imageData.data;

    const scaleX = srcW / w;
    const scaleY = srcH / h;

    for (let iy = 0; iy < h; iy++) {
      for (let ix = 0; ix < w; ix++) {
        const sx = Math.min(Math.floor(ix * scaleX), srcW - 1);
        const sy = Math.min(Math.floor(iy * scaleY), srcH - 1);
        const srcIdx = (sy * srcW + sx) * 4;
        const dstIdx = (iy * w + ix) * 4;

        if (srcData[srcIdx + 3] > 20) {
          data[dstIdx] = 255; data[dstIdx + 1] = 255; data[dstIdx + 2] = 255; data[dstIdx + 3] = 255;
        } else {
          data[dstIdx] = 0; data[dstIdx + 1] = 0; data[dstIdx + 2] = 0; data[dstIdx + 3] = 255;
        }
      }
    }

    octx.putImageData(imageData, 0, 0);
    return out.toDataURL('image/png').split(',')[1];
  }, []);

  // ── Clear mask ──
  const clearMask = () => {
    const canvas = maskCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setHasStrokes(false);
  };

  // ── File upload (up to 3 refs) ──
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }
    if (refImages.length >= 3) { toast.error('Up to 3 reference images'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setRefImages((prev) => [...prev, { b64: reader.result.split(',')[1], preview: reader.result }]);
    };
    reader.readAsDataURL(file);
    // Reset the file input so the user can pick the same file twice if needed
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const removeRefAt = (idx) => setRefImages((prev) => prev.filter((_, i) => i !== idx));
  const clearRefImage = () => { setRefImages([]); if (fileInputRef.current) fileInputRef.current.value = ''; };

  // ── Submit iteration ──
  const handleIterate = async () => {
    if (!feedback.trim() && refImages.length === 0) return;
    setError('');
    setIterating(true);
    try {
      const refsArray = refImages.map((r) => r.b64).filter(Boolean);
      const firstRef = refsArray[0] || null;
      // Build mode-specific payload. Both modes accept feedback +
      // optional mask + optional ref image(s) + source_url + engine.
      let payload;
      if (isPhotoshop) {
        const maskB64 = (maskEnabled && hasStrokes) ? generateMaskB64() : null;
        payload = {
          feedback: feedback.trim(),
          source_url: selectedImage?.url || undefined,
          engine: engineChoice,
          // Send both: array (preferred, new) AND singular (back-compat).
          // Backend will dedupe — singular only fires if array missing.
          ...(refsArray.length > 0 ? {
            reference_images_b64: refsArray,
            reference_image_b64: firstRef,
          } : {}),
          ...(maskB64 ? { mask_b64: maskB64 } : { text_only: true }),
        };
      } else {
        payload = {
          feedback: feedback.trim(),
          reference_image: firstRef || undefined,
          reference_images: refsArray.length > 0 ? refsArray : undefined,
          source_url: selectedImage?.url || undefined,
          target_field: targetField,
          // When the operator picked a Colour Preset, hard-signal the backend
          // to suppress cast/costume/env refs and add a "preserve content,
          // only re-grade colour" anchor. Without this gpt-image-2 reads the
          // grade prompt as a scene-build instruction and reconstructs the
          // shot from the strongest visual ref (verified bug 2026-05-27 PM).
          ...(colourPresetActive ? { colour_grade_only: true, colour_preset_id: colourPresetActive } : {}),
        };
        if (maskEnabled && hasStrokes) {
          const maskB64 = generateMaskB64();
          if (maskB64) payload.mask_b64 = maskB64;
        }
        payload.engine = preselectedEngine || process.env.REACT_APP_ITERATE_ENGINE || 'gpt-image-2';
      }
      await axios.post(`${API}${endpoints.iter(shotId)}`, payload);
      // Clear the colour-preset flag after a successful dispatch so the next
      // iterate (if any) doesn't accidentally inherit the "no refs" mode.
      setColourPresetActive(null);
      // Option D (Feb 2026): do NOT wipe feedback / refImages here.
      // Inputs persist while the user remains on the same R-version; they
      // only change when the user clicks a different version chip (handled
      // by the useEffect that watches `selectedImage.round`). This keeps
      // a director's last-typed prompt + uploaded refs visible so they can
      // refine and re-submit without re-typing or re-uploading.
      const engineLabel = payload.engine === 'gpt-image-2' ? 'Photo engine' : 'Sketch engine';
      const refLabel = refsArray.length > 1 ? ` with ${refsArray.length} refs` : '';
      toast.info(
        maskEnabled && hasStrokes
          ? `${engineLabel} editing masked region${refLabel}...`
          : `${engineLabel} iteration started${refLabel}...`
      );
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to start iteration';
      setError(msg);
      setIterating(false);
      toast.error(msg);
    }
  };

  // ── Select / Move to Panel ──
  const handleSelect = async () => {
    if (!selectedImage) return;
    try {
      // Phase 3 keyframe-VLM parity (2026-05-23) — version-aware select.
      // Hyperreal rows from `keyframe_hyperreal_versions[]` carry a
      // `version_id`; flipping the active pointer goes through
      // `/shots/:id/keyframe/set-active` (mirrors SketchSamModal's
      // sketch/set-active path) so downstream readers (animatic, Veo
      // i2v, Topaz, S-Log3 export) consume the operator's pick. Legacy
      // rows (no version_id) fall through to `/select-gemini-iteration`.
      const versionId = selectedImage.version_id;
      if (isHyperrealTarget && versionId) {
        const res = await axios.post(`${API}/shots/${shotId}/keyframe/set-active`, { version_id: versionId });
        toast.success('Active hyperreal keyframe updated');
        onSelect?.(res.data.keyframe_url);
        onClose?.();
        return;
      }
      const res = await axios.post(`${API}${endpoints.select(shotId)}`, {
        round: selectedImage.round,
        // Phase 3 keyframe-VLM parity follow-up (2026-05-26) — IGK
        // user-uploaded + iterate + Move-to-Panel was 400-ing because
        // backend defaulted to gemini_iterations[]; thread the target
        // through so the hyperreal_iterations[] array is consulted.
        target: isHyperrealTarget ? 'hyperreal' : 'keyframe',
        // 2026-07-02 — strip rows can outnumber the legacy iterations array
        // (versions without version_id); backend prefers this exact URL over
        // positional round lookup, killing "Invalid round index" 400s.
        image_url: selectedImage.url,
      });
      toast.success(res.data.message || (isPhotoshop ? 'Edit saved' : 'Image selected as keyframe'));
      const newUrl = res.data.keyframe_url || res.data.sketch_url || res.data.url;
      onSelect?.(newUrl);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Selection failed');
    }
  };

  // ── Keyframe-1 — earlier launch-frame iteration ──
  //
  // Same backend pipeline as the sketch workbench (Gemini continuity →
  // Opus 4.7 planner+prompt → gpt-image-2) but the result is appended
  // to `gemini_iterations[]` / `hyperreal_iterations[]` so this modal's
  // existing polling loop picks it up. Sketch-target workbenches
  // (`SketchSamModal`) keep using `/sketch/keyframe-minus-one`; this
  // route is keyframe-only and only enabled when `mode='keyframe'`.
  const [keyframeMinusOneRunning, setKeyframeMinusOneRunning] = useState(false);
  const shotDescription = (shot?.description || '').trim();
  const kfMinusOneSourceUrl = selectedImage?.url || currentUrl || originalUrl;
  const canKeyframeMinusOne = !isPhotoshop
    && !!shotDescription
    && !!kfMinusOneSourceUrl
    && !iterating
    && !keyframeMinusOneRunning;
  const handleKeyframeMinusOne = async () => {
    if (!canKeyframeMinusOne) return;
    const userInstruction = (feedback || '').trim();
    setKeyframeMinusOneRunning(true);
    setError('');
    try {
      const payload = {
        target_field: targetField,
        source_url: kfMinusOneSourceUrl,
      };
      if (userInstruction) payload.user_instruction = userInstruction;
      await axios.post(`${API}/shots/${shotId}/keyframe/keyframe-minus-one`, payload);
      toast.success('Keyframe-1 started — building launch frame…');
      // Clear the textbox so the operator doesn't accidentally re-use the
      // same instruction on a subsequent Iterate click.
      if (userInstruction) setFeedback('');
      // Engage the existing keyframe iteration polling loop. The backend
      // uses the same `gemini_iterating` (or `hyperreal_iterating`) flag
      // and the result lands in `gemini_iterations[]`, so polling picks
      // it up automatically when the bg task finishes.
      setIterating(true);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Keyframe-1 failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setKeyframeMinusOneRunning(false);
    }
  };

  const isSelected = (round) => selectedImage?.round === round;
  // Top frame is always the latest (what you iterate on)
  // 2026-06-25 — Big-image display must follow the thumbnail click.
  // Previously this was `currentUrl || originalUrl`, which left the
  // hero image stuck on the latest iteration even after tapping R4 etc.
  // The selection highlight updated but the displayed image did not —
  // most visible on mobile where there's no hover hint.
  const topFrameUrl = selectedImage?.url || currentUrl || originalUrl;
  // Round label for the hero frame title — must reflect the SELECTED
  // round, not the latest. -1 = R0 (Original), otherwise R{round+1}.
  const topFrameRoundLabel = (() => {
    if (!selectedImage) {
      return iterations.length > 0
        ? `R${iterations.length} — ${friendlyActionLabel(iterations[iterations.length - 1])}`
        : 'R0 — Original';
    }
    if (selectedImage.round === -1) return 'R0 — Original';
    const iter = iterations[selectedImage.round];
    return iter
      ? `R${selectedImage.round + 1} — ${friendlyActionLabel(iter)}`
      : `R${selectedImage.round + 1}`;
  })();

  const content = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-0 md:p-8"
      style={{ background: 'rgba(0,0,0,0.92)' }}
      onClick={onClose}
      data-testid="gemini-iteration-modal"
    >
      <div
        className="relative w-full max-w-5xl h-[95vh] md:max-h-[90vh] rounded-none md:rounded-2xl overflow-hidden flex flex-col"
        style={{ background: '#0d0618', border: '1px solid rgba(168, 85, 247, 0.25)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 flex-shrink-0">
          <div>
            <h2 className="text-sm font-bold text-white flex items-center gap-1.5">
              {isPhotoshop ? (
                <Sparkles className="w-4 h-4 text-yellow-400" />
              ) : preselectedEngine === 'gpt-image-2' ? (
                <Sparkles className="w-4 h-4 text-[#10A37F]" />
              ) : (
                <Wand2 className="w-4 h-4 text-purple-400" />
              )}
              {isPhotoshop
                ? 'AI Photoshop Workbench'
                : preselectedEngine === 'gpt-image-2'
                  ? 'GPT Image 2 Iteration Workbench'
                  : 'Gemini Iteration Workbench'}
            </h2>
            <p className="text-[10px] text-white/40 mt-0.5">
              {shot?.scene_number} — {shot?.shot_number} · {iterations.length} iteration{iterations.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Mask toggle */}
            <button
              onClick={() => { setMaskEnabled(!maskEnabled); if (maskEnabled) clearMask(); else setMaskTool('sam'); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${
                maskEnabled
                  ? 'bg-purple-500/25 text-purple-300 border border-purple-500/50'
                  : 'bg-white/5 text-white/40 border border-white/10 hover:bg-white/10'
              }`}
              data-testid="gemini-mask-toggle"
            >
              {maskEnabled ? <Paintbrush className="w-3 h-3" /> : <Crosshair className="w-3 h-3" />}
              {maskEnabled ? 'Mask: ON' : 'Mask Region'}
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="gemini-iter-close">
              <X className="w-5 h-5 text-white/60" />
            </button>
          </div>
        </div>

        <div className="flex-1 flex flex-col md:flex-row overflow-hidden min-h-0">
          {/* LEFT: Stacked frames — latest on top, oldest at bottom */}
          <div className="flex-1 overflow-y-auto p-3 md:p-4 min-h-0" ref={scrollRef} data-testid="gemini-iter-scroll">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
              </div>
            ) : (
              <>
                {/* Rounds filmstrip — horizontal, always visible at the top of
                    the scroll. Shows R0 (Original) + every iteration so the
                    operator can tap-jump to any round without scrolling
                    through full-size frames. Mirrors the sketch workbench
                    filmstrip pattern (2026-05-20 Rohit feedback). */}
                {(iterations.length > 0 || originalUrl) && (
                  <div
                    className="flex items-center gap-2 mb-3 px-2 py-2 rounded-xl overflow-x-auto"
                    style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.06)' }}
                    data-testid="gemini-iter-rounds-strip"
                  >
                    {originalUrl && originalUrl !== topFrameUrl && (
                      <button
                        onClick={() => setSelectedImage(isSelected(-1) ? null : { round: -1, url: originalUrl })}
                        className={`relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 transition-all duration-200 ${isSelected(-1) ? 'scale-105' : 'opacity-70 hover:opacity-100'}`}
                        style={isSelected(-1) ? { boxShadow: '0 0 0 2px #A855F7' } : { border: '1px solid rgba(255,255,255,0.1)' }}
                        data-testid="gemini-iter-strip-original"
                        title="Original (R0)"
                      >
                        <img src={originalUrl} alt="R0" className="w-full h-full object-cover" loading="lazy" />
                        <span className="absolute bottom-0 left-0 right-0 text-[8px] font-mono text-center py-0.5" style={{ background: 'rgba(0,0,0,0.7)', color: isSelected(-1) ? '#C084FC' : '#94A3B8' }}>R0</span>
                      </button>
                    )}
                    {iterations.map((iter, idx) => {
                      const isLatest = idx === iterations.length - 1;
                      const active = isSelected(idx) || (!selectedImage && isLatest);
                      const tileUrl = (targetField === 'keyframe_hyperreal_url' && (iter.slog3_v2_url || iter.slog3_url))
                        ? (iter.slog3_v2_url || iter.slog3_url)
                        : iter.image_url;
                      const isKf1 = !!iter.kf_minus_one;
                      const engStr = ((iter.engine) || '').toLowerCase();
                      const enhancorKind = engStr.startsWith('enhancor-')
                        ? (engStr.includes('realistic_skin') ? 'skin' : 'upscale')
                        : null;
                      const enhTitle = enhancorKind === 'skin'
                        ? 'Skin enhanced via Enhancor'
                        : (enhancorKind === 'upscale' ? 'Upscaled via Enhancor' : null);
                      // 2026-05-23 Rohit — surface provenance on every
                      // filmstrip thumb so it's obvious which entries are
                      // SAM iterations (edits) vs pre/post regen dumps vs
                      // Enhancor/KF-1. Colour + label follow the same
                      // language as SketchSamModal.
                      const src = (iter.source || '').toLowerCase();
                      const provenance = isKf1 ? { label: 'KF-1', color: '#FB7185', title: 'Keyframe-1 launch frame' }
                        : enhancorKind === 'skin'    ? { label: 'Skin', color: '#FBBF24', title: 'Skin enhanced via Enhancor' }
                        : enhancorKind === 'upscale' ? { label: 'Upscale', color: '#FBBF24', title: 'Upscaled via Enhancor' }
                        : src === 'pre_regen'        ? { label: 'PRE', color: '#94A3B8', title: 'Snapshot before Regenerate was clicked' }
                        : src === 'regen_current'    ? { label: 'REGEN', color: '#34D399', title: 'Newly regenerated keyframe' }
                        : src === 'sam_iter'         ? { label: 'SAM', color: '#A78BFA', title: 'SAM (mask) edit iteration' }
                        : src === 'text_iter'        ? { label: 'Text', color: '#60A5FA', title: 'Text-only edit iteration' }
                        : src === 'force_regen'      ? { label: 'Regen', color: '#34D399', title: 'Force re-roll iteration' }
                        : { label: `R${idx + 1}`, color: '#C084FC', title: `Iteration R${idx + 1}` };
                      return (
                        <button
                          key={idx}
                          onClick={() => setSelectedImage(isSelected(idx) ? null : { round: idx, url: iter.image_url, version_id: iter.version_id })}
                          data-testid={`iteration-tile-h-${idx}`}
                          className={`relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 transition-all duration-200 ${active ? 'scale-105' : 'opacity-70 hover:opacity-100'}`}
                          style={active ? { boxShadow: `0 0 0 2px ${provenance.color}` } : { border: '1px solid rgba(255,255,255,0.1)' }}
                          data-testid={`gemini-iter-strip-r${idx + 1}`}
                          title={enhTitle || provenance.title}
                        >
                          <img src={tileUrl} alt={provenance.label} className="w-full h-full object-cover" loading="lazy" />
                          {enhancorKind && (
                            <span
                              className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center"
                              style={{ background: 'rgba(245,158,11,0.92)', boxShadow: '0 0 0 1px rgba(0,0,0,0.5)' }}
                              data-testid={`gemini-iter-strip-r${idx + 1}-enhancor-badge`}
                            >
                              {enhancorKind === 'skin'
                                ? <Sparkles className="w-2 h-2 text-black" strokeWidth={3} />
                                : <Zap className="w-2 h-2 text-black" strokeWidth={3} />}
                            </span>
                          )}
                          <span className="absolute bottom-0 left-0 right-0 text-[8px] font-mono font-bold text-center py-0.5" style={{ background: 'rgba(0,0,0,0.8)', color: active ? provenance.color : provenance.color }}>
                            {provenance.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {iterating && (
                  <div className="flex items-center gap-2 p-3 rounded-xl border border-purple-500/30 mb-3" style={{ background: 'rgba(168,85,247,0.08)' }}>
                    <Loader2 className="w-4 h-4 text-purple-400 animate-spin flex-shrink-0" />
                    <span className="text-[11px] text-purple-300 font-medium">Generating...</span>
                  </div>
                )}

                {/* Top frame: current/latest — with mask canvas */}
                {topFrameUrl && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-purple-400/80">
                        {topFrameRoundLabel}
                        {maskEnabled && (hasStrokes ? ' · Region Selected' : ' · Paint or Click to Mask')}
                      </span>
                      {maskEnabled && hasStrokes && (
                        <button onClick={clearMask} className="flex items-center gap-1 px-2 py-1 rounded text-[9px] text-white/40 hover:text-white/60 bg-white/5 hover:bg-white/10 transition-all">
                          <Trash2 className="w-3 h-3" /> Clear
                        </button>
                      )}
                    </div>
                    <div
                      ref={containerRef}
                      className="relative rounded-lg overflow-hidden border-2 transition-colors touch-none mx-auto group/topframe"
                      style={{
                        width: containerSize ? containerSize.width : '100%',
                        height: containerSize ? containerSize.height : 'auto',
                        borderColor: maskEnabled && hasStrokes ? 'rgba(168,85,247,0.5)' : 'rgba(168,85,247,0.25)',
                        cursor: maskEnabled ? (iterating ? 'wait' : (maskTool === 'brush' ? 'cell' : 'default')) : 'default',
                      }}
                      onPointerDown={maskEnabled && maskTool === 'brush' ? handlePointerDown : undefined}
                      onPointerMove={maskEnabled && maskTool === 'brush' ? handlePointerMove : undefined}
                      onPointerUp={maskEnabled && maskTool === 'brush' ? handlePointerUp : undefined}
                      onPointerLeave={maskEnabled && maskTool === 'brush' ? handlePointerUp : undefined}
                    >
                      <img
                        ref={imgRef}
                        src={topFrameUrl}
                        alt="Keyframe"
                        className="absolute inset-0 w-full h-full select-none"
                        draggable={false}
                        onLoad={(e) => {
                          const nw = e.target.naturalWidth;
                          const nh = e.target.naturalHeight;
                          if (nw && nh && scrollRef.current) {
                            const parentRect = scrollRef.current.getBoundingClientRect();
                            const maxW = parentRect.width - 32;
                            const maxH = parentRect.height - 160;
                            const scale = Math.min(maxW / nw, maxH / nh, 1);
                            setContainerSize({ width: Math.round(nw * scale), height: Math.round(nh * scale) });
                          }
                          imgLoadedRef.current = true;
                          if (maskEnabled) setupCanvas();
                        }}
                      />
                      {maskEnabled && (
                        <canvas
                          ref={maskCanvasRef}
                          className="absolute inset-0 w-full h-full"
                          style={{ pointerEvents: 'none' }}
                          data-testid="gemini-mask-canvas"
                        />
                      )}
                      {samLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
                        </div>
                      )}
                      {/* Hover toolbar — Expand + Download (Feb 2026). Hidden
                          when mask mode is active to avoid intercepting paint
                          strokes. */}
                      {!maskEnabled && (
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover/topframe:opacity-100 transition-opacity z-20">
                          <button
                            onClick={(e) => { e.stopPropagation(); setLightboxUrl(topFrameUrl); }}
                            className="p-1.5 rounded-md bg-black/70 hover:bg-black/90 text-white/80 hover:text-white border border-white/10"
                            data-testid="topframe-expand-btn"
                            title="Click to expand"
                          >
                            <Maximize2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const r = !selectedImage
                                ? iterations.length
                                : (selectedImage.round === -1 ? 0 : selectedImage.round + 1);
                              handleDownload(topFrameUrl, `iteration_R${r}.png`);
                            }}
                            className="p-1.5 rounded-md bg-black/70 hover:bg-black/90 text-white/80 hover:text-white border border-white/10"
                            data-testid="topframe-download-btn"
                            title="Download image"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Mask tool controls — under top frame */}
                    {maskEnabled && (
                      <div className="flex flex-col gap-2 mt-2" style={{ maxWidth: containerSize ? containerSize.width : undefined }}>
                        <div className="flex items-center gap-1.5 mx-auto">
                          <button onClick={() => setMaskTool('sam')}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all ${
                              maskTool === 'sam'
                                ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50'
                                : 'bg-white/5 text-white/40 border border-white/10 hover:bg-white/10'
                            }`}
                            data-testid="gemini-tool-sam">
                            <Search className="w-3 h-3" /> SAM3 Select
                          </button>
                          <button onClick={() => setMaskTool('brush')}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all ${
                              maskTool === 'brush'
                                ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50'
                                : 'bg-white/5 text-white/40 border border-white/10 hover:bg-white/10'
                            }`}
                            data-testid="gemini-tool-brush">
                            <Paintbrush className="w-3 h-3" /> Brush
                          </button>
                        </div>
                        {maskTool === 'sam' && (
                          <div className="flex items-center gap-1.5">
                            <input
                              type="text" value={samPrompt} onChange={(e) => setSamPrompt(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter' && samPrompt.trim()) { e.preventDefault(); handleSAM3Segment(); } }}
                              placeholder="Type object name, e.g. 'cushion', 'jacket'..."
                              className="flex-1 text-xs px-2.5 py-1.5 text-white rounded-lg border border-white/10 outline-none focus:border-purple-500/50 transition-colors"
                              style={{ background: 'rgba(255,255,255,0.04)' }}
                              disabled={samLoading || iterating}
                              data-testid="gemini-sam3-prompt"
                            />
                            <button onClick={handleSAM3Segment}
                              disabled={samLoading || iterating || !samPrompt.trim()}
                              className="px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-purple-500/30 text-purple-300 border border-purple-500/50 hover:bg-purple-500/40 disabled:opacity-30 transition-all flex items-center gap-1"
                              data-testid="gemini-sam3-go">
                              {samLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                              Detect
                            </button>
                          </div>
                        )}
                        {maskTool === 'brush' && (
                          <div className="flex items-center gap-2">
                            <button onClick={() => setBrushSize(s => Math.max(8, s - 8))}
                              className="w-6 h-6 rounded flex items-center justify-center bg-white/5 border border-white/10 text-white/40 hover:bg-white/10"
                              data-testid="gemini-brush-decrease">
                              <Minus className="w-3 h-3" />
                            </button>
                            <input type="range" min="8" max="80" value={brushSize}
                              onChange={(e) => setBrushSize(Number(e.target.value))}
                              className="flex-1 h-1 accent-purple-500" data-testid="gemini-brush-slider" />
                            <span className="text-[10px] text-white/30 w-8 text-right">{brushSize}px</span>
                            <button onClick={() => setBrushSize(s => Math.min(80, s + 8))}
                              className="w-6 h-6 rounded flex items-center justify-center bg-white/5 border border-white/10 text-white/40 hover:bg-white/10"
                              data-testid="gemini-brush-increase">
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Previous iterations — stacked, newest first, same size */}
                {[...iterations].reverse().map((iter, revIdx) => {
                  const idx = iterations.length - 1 - revIdx;
                  // Skip the latest (already shown as top frame)
                  if (idx === iterations.length - 1) return null;
                  return (
                    <div key={idx} className="mb-4" data-testid={`gemini-iter-round-${idx}`}>
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider text-white/30 mb-1.5 block"
                        title={(iter.feedback || iter.prompt_used || '').trim()}
                      >
                        R{idx + 1}
                        <span className="text-purple-300/70 font-medium normal-case tracking-normal ml-2">
                          {friendlyActionLabel(iter)}
                        </span>
                      </span>
                      <div
                        className={`relative rounded-lg overflow-hidden border-2 mx-auto cursor-pointer transition-all group/itertile ${
                          isSelected(idx) ? 'border-purple-500 shadow-lg shadow-purple-500/20' : 'border-white/10 hover:border-white/25'
                        }`}
                        style={containerSize ? { width: containerSize.width, height: containerSize.height } : undefined}
                        onClick={() => setSelectedImage(isSelected(idx) ? null : { round: idx, url: iter.image_url, version_id: iter.version_id })}
                      >
                        {/* 2026-05-17 — for hyperreal keyframes, prefer the
                         * iteration's S-Log3 sibling for display so the
                         * thumbnail matches the workbench top frame.
                         * Falls back to Rec.709 when the sibling isn't
                         * present (legacy iterations pre-S-Log3 support,
                         * D1 = (c) graceful fallback). */}
                        {(() => {
                          const tileUrl = (targetField === 'keyframe_hyperreal_url'
                            && (iter.slog3_v2_url || iter.slog3_url))
                            ? (iter.slog3_v2_url || iter.slog3_url)
                            : iter.image_url;
                          return (
                            <img src={tileUrl} alt={`R${idx+1}`} className="w-full h-full object-cover" loading="lazy" />
                          );
                        })()}
                        {isSelected(idx) && (
                          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                        {/* Hover toolbar — Expand + Download (Feb 2026) */}
                        <div className="absolute top-1.5 left-1.5 flex gap-1 opacity-0 group-hover/itertile:opacity-100 transition-opacity z-20">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // 2026-05-17 — expand to S-Log3 (display layer) for hyperreal.
                              const expandUrl = (targetField === 'keyframe_hyperreal_url'
                                && (iter.slog3_v2_url || iter.slog3_url))
                                ? (iter.slog3_v2_url || iter.slog3_url)
                                : iter.image_url;
                              setLightboxUrl(expandUrl);
                            }}
                            className="p-1 rounded bg-black/70 hover:bg-black/90 text-white/80 hover:text-white border border-white/10"
                            data-testid={`iter-expand-${idx}`}
                            title="Click to expand"
                          >
                            <Maximize2 className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDownload(iter.image_url, `iteration_R${idx+1}.png`); }}
                            className="p-1 rounded bg-black/70 hover:bg-black/90 text-white/80 hover:text-white border border-white/10"
                            data-testid={`iter-download-${idx}`}
                            title="Download image"
                          >
                            <Download className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      {/* Ref thumbnails used to create this version (Layer 2 —
                          Feb 2026). Display-only here; clicking the version
                          chip itself restores them into the input fields via
                          the useEffect that watches selectedImage.round. */}
                      {Array.isArray(iter.reference_image_urls) && iter.reference_image_urls.length > 0 && (
                        <div className="flex items-center gap-1 mt-1.5 px-0.5" data-testid={`gemini-iter-round-refs-${idx}`}>
                          <span className="text-[9px] uppercase tracking-wider text-white/30 mr-0.5">refs:</span>
                          {iter.reference_image_urls.slice(0, 3).map((u, ri) => (
                            <img key={ri} src={u} alt={`ref ${ri + 1}`} className="w-6 h-6 rounded object-cover border border-white/10" loading="lazy" />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Original (R0) — at the bottom. Hidden when there are no
                    iterations yet (the top frame already IS the original in
                    that case, so we'd be rendering the same image twice and
                    confusing the round indexing). Also hidden when the
                    original URL equals the current URL — same reason. */}
                {originalUrl && iterations.length > 0 && originalUrl !== topFrameUrl && (
                  <div className="mb-4" data-testid="gemini-iter-round-original">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white/30 mb-1.5 block">R0 — Original</span>
                    <div
                      className={`relative rounded-lg overflow-hidden border-2 mx-auto cursor-pointer transition-all group/itertile ${
                        isSelected(-1) ? 'border-purple-500 shadow-lg shadow-purple-500/20' : 'border-white/10 hover:border-white/25'
                      }`}
                      style={containerSize ? { width: containerSize.width, height: containerSize.height } : undefined}
                      onClick={() => setSelectedImage(isSelected(-1) ? null : { round: -1, url: originalUrl })}
                    >
                      <img src={originalUrl} alt="Original" className="w-full h-full object-cover" loading="lazy" />
                      {isSelected(-1) && (
                        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      {/* Hover toolbar — Expand + Download (Feb 2026) */}
                      <div className="absolute top-1.5 left-1.5 flex gap-1 opacity-0 group-hover/itertile:opacity-100 transition-opacity z-20">
                        <button
                          onClick={(e) => { e.stopPropagation(); setLightboxUrl(originalUrl); }}
                          className="p-1 rounded bg-black/70 hover:bg-black/90 text-white/80 hover:text-white border border-white/10"
                          data-testid="iter-expand-original"
                          title="Click to expand"
                        >
                          <Maximize2 className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownload(originalUrl, 'iteration_R0_original.png'); }}
                          className="p-1 rounded bg-black/70 hover:bg-black/90 text-white/80 hover:text-white border border-white/10"
                          data-testid="iter-download-original"
                          title="Download image"
                        >
                          <Download className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* RIGHT: Controls */}
          <div className="w-full md:w-72 border-t md:border-t-0 md:border-l border-white/10 flex flex-col flex-shrink-0 max-h-[45vh] md:max-h-none" style={{ background: 'rgba(10,4,20,0.6)' }}>
            {error && (
              <div className="px-4 pt-3">
                <div className="p-2.5 rounded-lg text-[10px] text-red-400 border border-red-500/20" style={{ background: 'rgba(239,68,68,0.08)' }}>{error}</div>
              </div>
            )}

            <div className="flex-1 flex flex-col justify-end p-4 overflow-y-auto">
              <div className="mb-3">
                <label className="text-[10px] font-medium text-white/50 uppercase tracking-wider mb-2 block">
                  Director's Feedback
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFeedback(v);
                    // If the operator hand-edits the preset prompt, drop the
                    // colour-grade-only flag so the next iterate behaves as a
                    // normal feedback edit (with cast/costume/env refs).
                    if (colourPresetActive) {
                      const matched = COLOUR_PRESETS.find(p => p.id === colourPresetActive);
                      if (!matched || v.trim() !== matched.prompt.trim()) {
                        setColourPresetActive(null);
                      }
                    }
                  }}
                  placeholder={maskEnabled ? "e.g. 'Change jacket to leather' or 'Remove the hat'..." : "e.g. 'Add lens flare' or 'Make shadows deeper'..."}
                  className="w-full h-20 text-xs px-3 py-2.5 text-white rounded-xl border border-white/10 outline-none resize-none focus:border-purple-500/50 transition-colors"
                  style={{ background: 'rgba(255,255,255,0.04)' }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && (feedback.trim() || refImage)) { e.preventDefault(); handleIterate(); } }}
                  disabled={iterating}
                  data-testid="gemini-iter-feedback-input"
                />
                {colourPresetActive && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-[9px] text-blue-300/80">
                    <Palette className="w-3 h-3" />
                    <span data-testid="colour-preset-active-hint">Colour-grade only — cast/costume/env refs disabled for this iterate</span>
                  </div>
                )}
              </div>

              {/* Reference Image Upload — up to 3 slots */}
              <div className="mb-3">
                <label className="text-[10px] font-medium text-white/50 uppercase tracking-wider mb-1.5 block">
                  Reference Images <span className="text-white/30 normal-case">(optional · up to 3)</span>
                </label>
                <input type="file" ref={fileInputRef} accept="image/*" onChange={handleFileSelect} className="hidden" data-testid="gemini-iter-ref-upload" />
                <div className="grid grid-cols-3 gap-2">
                  {[0, 1, 2].map((idx) => {
                    const r = refImages[idx];
                    if (r) {
                      return (
                        <div key={idx} className="relative rounded-lg overflow-hidden border border-purple-500/30" data-testid={`gemini-iter-ref-slot-${idx}`}>
                          <img src={r.preview} alt={`Ref ${idx + 1}`} className="w-full h-20 object-cover" />
                          <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-[9px] text-white/90 font-semibold">#{idx + 1}</div>
                          <button onClick={() => removeRefAt(idx)}
                            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center hover:bg-red-500/80 transition-colors"
                            data-testid={`gemini-iter-ref-remove-${idx}`}>
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      );
                    }
                    // First empty slot is the active "upload" button; later
                    // slots are visible-but-passive placeholders until the
                    // previous slots fill up. This is the cleanest mental
                    // model: "upload, upload, upload — in order."
                    const isNextSlot = refImages.length === idx;
                    return (
                      <button
                        key={idx}
                        onClick={isNextSlot ? () => fileInputRef.current?.click() : undefined}
                        disabled={!isNextSlot}
                        className={`h-20 rounded-lg border border-dashed flex flex-col items-center justify-center gap-1 transition-all ${
                          isNextSlot
                            ? 'border-white/15 text-white/40 hover:text-white/70 hover:border-white/30 cursor-pointer'
                            : 'border-white/5 text-white/20 cursor-not-allowed'
                        }`}
                        data-testid={`gemini-iter-ref-add-${idx}`}
                      >
                        <ImagePlus className="w-3.5 h-3.5" />
                        <span className="text-[9px]">Ref {idx + 1}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {maskEnabled && hasStrokes && (
                <div className="mb-3 flex items-center gap-2 p-2 rounded-lg border border-purple-500/20" style={{ background: 'rgba(168,85,247,0.08)' }}>
                  <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
                  <span className="text-[10px] text-purple-300/80">Masked — edit targets only the purple area</span>
                </div>
              )}

              {/* Per-iteration Engine Picker (AI Photoshop + future
                  non-Insanely-Good keyframe iteration paths). Hidden
                  when engineLocked=true. */}
              {showEnginePicker && (
                <div className="mb-3" data-testid="engine-picker">
                  <label className="text-[10px] font-medium text-white/50 uppercase tracking-wider mb-2 block">
                    Edit Engine
                  </label>
                  <div className="flex gap-1 p-1 rounded-lg" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <button
                      onClick={() => setEngineChoice('gpt-image-2')}
                      disabled={iterating}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-[10px] font-semibold transition-all ${
                        engineChoice === 'gpt-image-2'
                          ? 'bg-emerald-500/20 text-emerald-300 shadow-sm ring-1 ring-emerald-500/40'
                          : 'text-white/40 hover:text-white'
                      }`}
                      data-testid="engine-pick-gpt-image-2"
                      title="Photo engine — sharper photo edits, slower (~25s), better at preserving fine detail."
                    >
                      <Sparkles className="w-3 h-3" /> GPT-Image-2
                    </button>
                    <button
                      onClick={() => setEngineChoice('nano-banana')}
                      disabled={iterating}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-[10px] font-semibold transition-all ${
                        engineChoice === 'nano-banana'
                          ? 'bg-purple-500/20 text-purple-300 shadow-sm ring-1 ring-purple-500/40'
                          : 'text-white/40 hover:text-white'
                      }`}
                      data-testid="engine-pick-nano-banana"
                      title="Sketch engine — faster (~8s), more creative variance, better for abstract/stylised edits."
                    >
                      <Wand2 className="w-3 h-3" /> Nano Banana
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={handleIterate}
                disabled={iterating || (!feedback.trim() && !refImage)}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold transition-all disabled:opacity-30"
                style={{ background: iterating ? 'rgba(168,85,247,0.2)' : (preselectedEngine === 'gpt-image-2' ? 'linear-gradient(135deg, #10A37F, #0D7F63)' : 'linear-gradient(135deg, #A855F7, #7C3AED)'), color: 'white' }}
                data-testid="gemini-iter-submit-btn"
              >
                {iterating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {iterating ? 'Generating...' : maskEnabled && hasStrokes ? 'Edit Masked Region' : 'Iterate'}
              </button>

              {/* ── Enhancor + Colour Presets dropdowns (May 2026) ────────
                  Two adjacent dropdowns under the primary Iterate CTA:
                  • Enhance — fires an Enhancor service immediately on
                    selection. Status polls every 4s; completed result
                    auto-appears in the iterations strip as the new Rn.
                  • Colour Presets — click → fills the textbox above so
                    operator can tweak then click Iterate. */}
              <div className="mt-2 grid grid-cols-2 gap-2">
                {/* Enhance */}
                <div className="relative">
                  <button
                    onClick={() => { setEnhanceOpen(o => !o); setPresetsOpen(false); }}
                    disabled={iterating || !!enhancePending || !!(enhanceJob && !['COMPLETED','FAILED','TIMEOUT'].includes(enhanceJob.status))}
                    className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-semibold transition-all disabled:opacity-50"
                    style={{ background: 'rgba(245, 158, 11, 0.10)', color: '#FBBF24', border: '1px solid rgba(245, 158, 11, 0.30)' }}
                    data-testid="workbench-enhance-btn"
                    title="Send the active frame through Enhancor AI for skin polish or upscale. Adds the result as a new version (Rn)."
                  >
                    {enhancePending ? (
                      <><Loader2 className="w-3 h-3 animate-spin" /> queueing…</>
                    ) : enhanceJob && !['COMPLETED','FAILED','TIMEOUT'].includes(enhanceJob.status) ? (
                      <><Loader2 className="w-3 h-3 animate-spin" /> {enhanceJob.status.toLowerCase()}…</>
                    ) : (
                      <><Zap className="w-3 h-3" /> Enhance <ChevronDown className="w-3 h-3 opacity-60" /></>
                    )}
                  </button>
                  {enhanceOpen && (
                    <div
                      className="absolute z-50 mt-1 left-0 right-0 rounded-lg shadow-2xl overflow-hidden"
                      style={{ background: 'rgba(20, 20, 22, 0.98)', border: '1px solid rgba(245, 158, 11, 0.25)' }}
                      data-testid="enhance-dropdown"
                    >
                      {ENHANCOR_SERVICES.map(s => (
                        <button
                          key={s.id}
                          onClick={() => triggerEnhance(s.id)}
                          className="w-full text-left px-3 py-2 hover:bg-amber-500/10 transition-colors border-b border-white/5 last:border-b-0"
                          data-testid={`enhance-opt-${s.id}`}
                        >
                          <div className="text-[11px] font-semibold text-amber-300">{s.label}</div>
                          <div className="text-[9px] text-white/40 mt-0.5">{s.desc}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Colour Presets */}
                <div className="relative">
                  <button
                    onClick={() => { setPresetsOpen(o => !o); setEnhanceOpen(false); }}
                    disabled={iterating}
                    className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-semibold transition-all disabled:opacity-50"
                    style={{ background: 'rgba(59, 130, 246, 0.10)', color: '#93C5FD', border: '1px solid rgba(59, 130, 246, 0.30)' }}
                    data-testid="workbench-presets-btn"
                    title="Fill the iterate textbox with a canned colour-grade prompt. Tweak then click Iterate."
                  >
                    <Palette className="w-3 h-3" /> Colour Preset <ChevronDown className="w-3 h-3 opacity-60" />
                  </button>
                  {presetsOpen && (
                    <div
                      className="absolute z-50 mt-1 left-0 right-0 rounded-lg shadow-2xl overflow-hidden max-h-72 overflow-y-auto"
                      style={{ background: 'rgba(20, 20, 22, 0.98)', border: '1px solid rgba(59, 130, 246, 0.25)' }}
                      data-testid="presets-dropdown"
                    >
                      {COLOUR_PRESETS.map(p => (
                        <button
                          key={p.id}
                          onClick={() => {
                            setFeedback(p.prompt);
                            setColourPresetActive(p.id);
                            setPresetsOpen(false);
                            toast.success(`Loaded: ${p.label} (colour-grade only — refs disabled)`);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-blue-500/10 transition-colors border-b border-white/5 last:border-b-0"
                          data-testid={`preset-opt-${p.id}`}
                        >
                          <div className="text-[11px] font-semibold text-blue-300">{p.label}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Keyframe-1 — earlier launch-frame iteration (M-12 · keyframe target) */}
              {!isPhotoshop && (
                <div className="mt-2">
                  <button
                    onClick={handleKeyframeMinusOne}
                    disabled={!canKeyframeMinusOne}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-[11px] font-bold transition-all disabled:opacity-30"
                    style={{ background: 'rgba(251,113,133,0.08)', color: '#FB7185', border: '1px solid rgba(251,113,133,0.25)' }}
                    data-testid="workbench-keyframe-minus-one-btn"
                    title={
                      !shotDescription
                        ? 'Keyframe-1 needs a shot description to plan the launch frame.'
                        : 'Generate an EARLIER launch frame from the active image. Uses the shot description automatically; any text in the box above is treated as an optional hint to the planner. ~30-60s.'
                    }
                  >
                    {keyframeMinusOneRunning ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating launch frame…</>
                    ) : (
                      <><Rewind className="w-3.5 h-3.5" /> Keyframe-1 (earlier launch frame)</>
                    )}
                  </button>
                  <p className="text-[10px] text-white/40 mt-1.5 leading-relaxed">
                    {shotDescription
                      ? 'Generates the earliest plausible launch frame of this shot’s motion arc — ideal as an I2V starting frame. Type an optional hint above (e.g. "doors fully shut") to steer the planner, or leave it blank to let it pick.'
                      : 'Disabled — this shot has no description, which Keyframe-1 needs to plan the launch frame.'}
                  </p>
                </div>
              )}

              {/* "★ Add to Gallery" — AI Photoshop only. Adds the
                  currently-displayed iteration (top frame OR selected
                  thumbnail) to the user's persistent gallery WITHOUT
                  closing the workbench, so multiple frames from the
                  same session can be saved. */}
              {onAddToGallery && (
                <button
                  onClick={() => {
                    const round = selectedImage ? selectedImage.round : iterations.length - 1;
                    const url = selectedImage?.url || topFrameUrl;
                    if (!url) return;
                    const iter = round >= 0 ? iterations[round] : null;
                    onAddToGallery({
                      image_url: url,
                      edit_instruction: iter?.feedback || feedback || '',
                      source_url: iter?.source_url || originalUrl,
                      original_url: originalUrl,
                      engine: iter?.engine || engineChoice,
                    });
                  }}
                  disabled={iterating || !(selectedImage?.url || topFrameUrl)}
                  className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-xl text-[11px] font-bold transition-all disabled:opacity-30"
                  style={{ background: 'rgba(250,204,21,0.08)', color: '#FACC15', border: '1px solid rgba(250,204,21,0.2)' }}
                  data-testid="add-to-gallery-button"
                  title="Add this iteration to your AI Photoshop Gallery. The workbench stays open so you can keep iterating."
                >
                  <Sparkles className="w-3 h-3" /> ★ Add to Gallery
                </button>
              )}
            </div>

            {/* Move to Panel */}
            {selectedImage && (
              <div className="border-t border-white/10 p-4" data-testid="gemini-iter-selection-panel">
                <div className="flex items-center gap-2 mb-2">
                  <img src={selectedImage.url} alt="Selected" className="w-12 h-8 rounded object-cover border border-purple-500/50" />
                  <p className="text-[10px] font-medium text-white truncate">
                    {selectedImage.round === -1 ? 'Original' : `Round ${selectedImage.round + 1}`}
                  </p>
                </div>
                <button onClick={handleSelect}
                  className="w-full py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5"
                  style={{ background: 'linear-gradient(135deg, #A855F7, #7C3AED)', color: 'white' }}
                  data-testid="gemini-iter-move-to-panel-btn"
                >
                  <Check className="w-3.5 h-3.5" /> {isPhotoshop ? 'Use This Edit' : 'Move to Panel'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox overlay — Feb 2026 */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
          style={{ background: 'rgba(0,0,0,0.92)' }}
          onClick={() => setLightboxUrl(null)}
          data-testid="iter-lightbox-overlay"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20"
            data-testid="iter-lightbox-close"
            title="Close (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDownload(lightboxUrl, 'iteration_fullsize.png'); }}
            className="absolute top-4 right-16 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20"
            data-testid="iter-lightbox-download"
            title="Download image"
          >
            <Download className="w-5 h-5" />
          </button>
          <img
            src={lightboxUrl}
            alt="Expanded view"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );

  return typeof document !== 'undefined' ? ReactDOM.createPortal(content, document.body) : null;
}
